import AutoStoriesOutlined from '@mui/icons-material/AutoStoriesOutlined';
import KeyIcon from '@mui/icons-material/Key';
import Logout from '@mui/icons-material/Logout';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SupportAgent from '@mui/icons-material/SupportAgent';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useContext, useState } from 'react';

import { useAnalytics } from '~/analytics/useAnalytics';
import { UserContext } from '~/auth/userContext';
import { DOCS_AND_GUIDES_LABEL, DOCS_AND_GUIDES_URL } from '~/components/docs/docsAndGuides';
import { SupportRequestDialog } from '~/components/support/SupportRequestDialog';
import { BUTTON_RADIUS } from '~/theme';

// Reduce a stored full name down to "First Last" (dropping any middle names).
const toFirstLast = (fullName?: string): string => {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1]}`;
};

const toInitials = (fullName?: string): string => {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return (parts[0][0] ?? '').toUpperCase();
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
};

/**
 * Who you are signed in as, and the account actions behind it: help, support and log out.
 * These were three rows in the navigation rail, where they read as destinations.
 */
export function AccountMenu() {
  const { track } = useAnalytics();
  const {
    user: { id, email },
    userDetails: { fullName },
  } = useContext(UserContext);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const close = () => setAnchor(null);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);

  if (id === null) return null;

  const displayName = toFirstLast(fullName);
  const initials = toInitials(fullName);

  return (
    <>
      <Tooltip title={displayName} describeChild>
        <ButtonBase
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label={`${displayName} — account and support`}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          data-testid="account-menu-button"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: 36,
            px: 0.5,
            borderRadius: `${BUTTON_RADIUS}px`,
            // The bar is a white `MuiAppBar` still set to `color="primary"`, so it hands
            // children `primary.contrastText` — white on white. Take the bar's own ink.
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
            {initials}
          </Avatar>
          <MoreVertIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
        </ButtonBase>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ list: { dense: true, sx: { minWidth: 220 } } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {displayName}
          </Typography>
          {email ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">
              {email}
            </Typography>
          ) : null}
        </Box>
        <Divider />
        <MenuItem
          component="a"
          href={DOCS_AND_GUIDES_URL}
          target="_blank"
          rel="noopener"
          onClick={() => {
            track('help_center_clicked', { source: 'account_menu' });
            close();
          }}
        >
          <ListItemIcon>
            <AutoStoriesOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>{DOCS_AND_GUIDES_LABEL}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            track('support_request_opened');
            setSupportDialogOpen(true);
            close();
          }}
        >
          <ListItemIcon>
            <SupportAgent fontSize="small" />
          </ListItemIcon>
          <ListItemText>Contact Support</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem component={Link} href="/account/password" onClick={close}>
          <ListItemIcon>
            <KeyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Set a password</ListItemText>
        </MenuItem>
        <MenuItem component={Link} href="/logout" onClick={close}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
      </Menu>

      <SupportRequestDialog open={supportDialogOpen} onClose={() => setSupportDialogOpen(false)} />
    </>
  );
}
